// FLAGS: silent-failure
// The web app's only feature-flag read: one GET per key against OpenUp.Api's `v1/featureflags`. Fails
// closed in both directions — initial state false, and any error false.
import { apiPaths } from "@src/utils/constants";
import axios from "axios";
import { useEffect, useState } from "react";

// FLAGS: silent-failure
// Every error — network, 4xx, 5xx, a non-boolean body — becomes `false` with nothing reported, so a
// flag-service outage is indistinguishable from a flag that is off and the gated feature just disappears.
export const getFeatureFlagValue = async (key: string): Promise<boolean> => {
  try {
    const response = await axios.get(`${apiPaths.featureFlags}/${key}`);
    const featureFlagValue = response.data as boolean;
    return featureFlagValue === true;
  } catch (error) {
    return false;
  }
};

// FLAGS: silent-failure
// Returns a bare boolean with no loading signal, so the first render is always `false` — a caller cannot
// tell "off" from "not answered yet". Use `useFeatureFlagWithLoading` where that difference matters.
export const useFeatureFlag = (key: string): boolean => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchFlag = async () => {
      const value = await getFeatureFlagValue(key);
      if (isMounted) {
        setEnabled(value);
      }
    };

    fetchFlag();

    return () => {
      isMounted = false;
    };
  }, [key]);

  return enabled;
};

export const useFeatureFlagWithLoading = (
  key: string,
): { enabled: boolean; loading: boolean } => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchFlag = async () => {
      const value = await getFeatureFlagValue(key);
      if (isMounted) {
        setEnabled(value);
        setLoading(false);
      }
    };

    fetchFlag();

    return () => {
      isMounted = false;
    };
  }, [key]);

  return { enabled, loading };
};
